import '../documents.css';

import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../../recoil/atoms/userConfigState';
import { teamsState } from '../../../recoil/atoms/teamsState';
import { navigationPropsState } from '../../../recoil/atoms/navigationPropsState';
import { useSetNavigationProps } from '../../../recoil/hooks/useSetNavigationProps';
import { useAuthContext } from '../../../../common/contexts/useAuthContext';
import { trackEvent } from '../../../services/server/analytics/trackEvent';

import { translate } from '../../../../common/language/translations';
import { getTenantColor } from '../../../static/tenantConfigs';
import { Toggle } from '../../controls/input/Toggle';
import { getTeamIcon } from '../../../utils/iconUtils';
import { dateStringToTextFormat } from '../../../utils/dateUtils';
import { ArchivedTeam } from '../../../types/team';
import { getDisplayTeamName } from '../../../utils/nameUtils';


interface ExistingArchivedTeamsProps {
  archivedTeams: ArchivedTeam[];
  selectedArchivedTeam: ArchivedTeam | undefined;
  handleArchivedTeamClicked: (archivedTeam: ArchivedTeam | undefined) => void;
}


export const ExistingArchivedTeams: React.FC<ExistingArchivedTeamsProps> = ({
  archivedTeams,
  selectedArchivedTeam,
  handleArchivedTeamClicked
}) => {

  const { currentUser } = useAuthContext();

  const userConfig = useRecoilValue(userConfigState);

  const teams = useRecoilValue(teamsState);

  const navigationProps = useRecoilValue(navigationPropsState);
  const updateNavigationProps = useSetNavigationProps();


  const handleSelectArchivedTeamAndTrackEvent = (archivedTeam: ArchivedTeam) => {
    if (archivedTeam.id === selectedArchivedTeam?.id) {
      handleArchivedTeamClicked(undefined);
    }
    else {
      trackEvent('ArchivedTeamViewed', {}, currentUser, 'user');
      handleArchivedTeamClicked(archivedTeam);
    }
  };


  const getArchivedTeamClassName = (archivedTeam: ArchivedTeam) => {
    let baseClassName = 'existing-document existing-archived-team';

    if (archivedTeam.isDeleted) {
      baseClassName += ' existing-document-deleted';
    }
    if (archivedTeam.id === selectedArchivedTeam?.id) {
      baseClassName += ' existing-document-selected';
    }

    return baseClassName;
  };


  const tenantColor = getTenantColor(userConfig?.club ?? '');

  return (
    <div className='existing-documents-container'>

      <div className='existing-documents-title'>
        {translate('allArchivedTeams', userConfig?.language)}
      </div>

      <div className='existing-documents-divider existing-documents-top-divider' />

      <div className='existing-documents-section existing-documents-section-single-toggle'>
        {archivedTeams.length === 0 && (
          <div className='no-existing-documents-title'>
            {translate('noArchivedTeams', userConfig?.language)}
          </div>
        )}
        {archivedTeams.map(archivedTeam => {
          if (!navigationProps.showDeletedArchivedTeams && archivedTeam['isDeleted']) return null;

          const title = getDisplayTeamName(archivedTeam.teamId, teams, userConfig?.language, archivedTeam.name) ?? '';

          return (
            <div
              key={archivedTeam.id}
              className={getArchivedTeamClassName(archivedTeam)}
              onClick={() => handleSelectArchivedTeamAndTrackEvent(archivedTeam)}
            >
              {getTeamIcon(archivedTeam.teamId, 22, '#ffffff', archivedTeam.isAcademyTeam)}

              <div className='existing-document-vertical-divider' style={{ backgroundColor: tenantColor }} />

              <div className='flex-column'>
                <div className='flex-row'>
                  <div className='existing-document-title' style={{ fontSize: title.length > 20 ? 10 : title.length > 16 ? 11 : title.length > 12 ? 12 : 13 }}>
                    {title}
                  </div>
                  <div className='existing-archived-teams-date'>
                    {dateStringToTextFormat(archivedTeam['date'].split('T')[0])}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className='existing-documents-divider existing-documents-bottom-divider-single-toggle' />

      <div className='existing-document-toggle-container existing-document-toggle-bottom-container'>
        <div className='existing-document-toggle-section' style={{ width: 165 }}>
          <div className='existing-document-toggle-title'>
            {translate('showDeletedArchivedTeams', userConfig?.language)}
          </div>
          <div className='existing-document-toggle'>
            <Toggle
              isToggled={navigationProps.showDeletedArchivedTeams ?? false}
              setIsToggled={() => updateNavigationProps({ showDeletedArchivedTeams: !(navigationProps.showDeletedArchivedTeams ?? false) })}
            />
          </div>
        </div>
      </div>

    </div>
  );
};
